// Confirmation Modal for Approve/Reject Actions (VAC-8)

import React, { useState } from 'react'
import { X, AlertTriangle } from 'lucide-react'
import type { ApprovalQueueItem } from '@/types/vacation'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface ApprovalConfirmationModalProps {
  request: ApprovalQueueItem | null
  mode: 'approve' | 'reject' | null
  isOpen: boolean
  isLoading?: boolean
  onClose: () => void
  onConfirm: (notes?: string, reason?: string) => void
}

export const ApprovalConfirmationModal: React.FC<ApprovalConfirmationModalProps> = ({
  request,
  mode,
  isOpen,
  isLoading = false,
  onClose,
  onConfirm
}) => {
  const [approvalNotes, setApprovalNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [validationError, setValidationError] = useState('')

  if (!isOpen || !request || !mode) return null

  const handleConfirm = () => {
    // Validate rejection reason
    if (mode === 'reject') {
      if (!rejectionReason.trim()) {
        setValidationError('Motivo da rejeição é obrigatório')
        return
      }
      if (rejectionReason.trim().length < 10) {
        setValidationError('Motivo deve ter pelo menos 10 caracteres')
        return
      }
    }

    setValidationError('')

    if (mode === 'approve') {
      onConfirm(approvalNotes || undefined)
    } else {
      onConfirm(undefined, rejectionReason)
    }
  }

  const formatDate = (isoDate: string): string => {
    return format(parseISO(isoDate), "dd 'de' MMMM", { locale: ptBR })
  }

  const isApprove = mode === 'approve'

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="modal-content max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-brand-gray/20">
            <h2
              id="modal-title"
              className={`text-xl font-display font-bold ${
                isApprove ? 'text-success' : 'text-error'
              }`}
            >
              {isApprove ? 'Aprovar Férias' : 'Rejeitar Férias'}
            </h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-brand-gray hover:text-white transition-colors"
              aria-label="Fechar modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Employee Info */}
            <div className="text-center">
              <p className="text-lg font-display font-medium text-white">
                {request.employeeName}
              </p>
              <p className="text-sm text-brand-gray">
                {request.employeeDepartment || 'Colaborador'}
              </p>
            </div>

            {/* Request Summary */}
            <div className="bg-brand-dark-lighter rounded-monumenta p-4">
              <div className="text-center space-y-1">
                <p className="text-sm text-brand-gray">
                  {request.totalDays} {request.totalDays === 1 ? 'dia' : 'dias'}
                </p>
                <p className="text-base font-medium text-white">
                  {formatDate(request.startDate)} a {formatDate(request.endDate)}
                </p>
              </div>
            </div>

            {/* Warning for Rejection */}
            {!isApprove && (
              <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/30 rounded-monumenta">
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white">
                  O colaborador será notificado da rejeição e o motivo informado.
                </p>
              </div>
            )}

            {/* Approval Notes (Optional) */}
            {isApprove && (
              <div>
                <label
                  htmlFor="approval-notes"
                  className="block text-sm font-medium text-white mb-2"
                >
                  Observações (opcional)
                </label>
                <textarea
                  id="approval-notes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  disabled={isLoading}
                  placeholder="Adicione observações sobre a aprovação..."
                  className="w-full px-3 py-2 border border-brand-gray/20 rounded-monumenta focus:ring-2 focus:ring-success focus:border-transparent bg-brand-dark-lighter text-white placeholder-brand-gray disabled:opacity-50"
                />
                <p className="text-xs text-brand-gray mt-1 text-right">
                  {approvalNotes.length}/500
                </p>
              </div>
            )}

            {/* Rejection Reason (Required) */}
            {!isApprove && (
              <div>
                <label
                  htmlFor="rejection-reason"
                  className="block text-sm font-medium text-white mb-2"
                >
                  Motivo da Rejeição <span className="text-error">*</span>
                </label>
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value)
                    setValidationError('')
                  }}
                  maxLength={500}
                  rows={4}
                  disabled={isLoading}
                  placeholder="Explique o motivo da rejeição (mínimo 10 caracteres)..."
                  className={`w-full px-3 py-2 border rounded-monumenta focus:ring-2 focus:ring-error focus:border-transparent bg-brand-dark-lighter text-white placeholder-brand-gray disabled:opacity-50 ${
                    validationError
                      ? 'border-error'
                      : 'border-brand-gray/20'
                  }`}
                />
                <div className="flex justify-between items-center mt-1">
                  {validationError && (
                    <p className="text-xs text-error">{validationError}</p>
                  )}
                  <p className="text-xs text-brand-gray ml-auto">
                    {rejectionReason.length}/500
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-brand-gray/20">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`flex-1 ${isApprove ? 'btn-primary' : 'btn-danger'}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processando...
                </span>
              ) : isApprove ? (
                'Confirmar Aprovação'
              ) : (
                'Confirmar Rejeição'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
